import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getServices, createBooking, getCurrentUser } from '../services/pocketbase';
import BookingCalendar from './BookingCalendar';
import { FiCheckCircle, FiAlertCircle, FiUser, FiSmartphone, FiMail, FiScissors, FiCalendar, FiMessageSquare, FiChevronDown, FiChevronUp, FiPlus, FiX } from 'react-icons/fi';
import './BookingFormPB.css';

const BookingFormPB = () => {
    const navigate = useNavigate();
    const user = getCurrentUser();

    // États
    const [step, setStep] = useState(1);
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);

    // Sélection UI
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Données du formulaire
    const [formData, setFormData] = useState({
        date: null,
        time: null,
        client_name: user?.name || '',
        client_email: user?.email || '',
        client_phone: '',
        notes: ''
    });

    // Charger les services et extraire les catégories
    useEffect(() => {
        const loadServices = async () => {
            try {
                const data = await getServices();
                const activeServices = data.filter(s => s.active);
                setServices(activeServices);

                // Extraction dynamique des catégories
                const uniqueCategories = [];
                const seenIds = new Set();

                activeServices.forEach(s => {
                    if (s.expand?.category) {
                        const cat = s.expand.category;
                        if (!seenIds.has(cat.id)) {
                            seenIds.add(cat.id);
                            uniqueCategories.push(cat);
                        }
                    }
                    // Fallback si pas de catégorie expand (gestion legacy ou sans cat)
                    else if (s.category) {
                        // On ne peut pas facilement deviner le nom si pas expand, on ignore ou on met "Autre"
                    }
                });
                // Tri par ordre si dispo
                setCategories(uniqueCategories.sort((a, b) => (a.order || 0) - (b.order || 0)));
            } catch (err) {
                console.error(err);
                setError("Impossible de charger les services.");
            }
        };
        loadServices();
    }, []);

    // Auth sync
    useEffect(() => {
        const u = getCurrentUser();
        if (u) {
            setFormData(prev => ({
                ...prev,
                client_name: prev.client_name || u.name,
                client_email: prev.client_email || u.email
            }));
        }
    }, [step]);

    // Gestionnaires de sélection
    const toggleCategory = (catId) => {
        if (selectedCategoryIds.includes(catId)) {
            setSelectedCategoryIds(prev => prev.filter(id => id !== catId));
            // Optionnel : Désélectionner les services de cette catégorie ? Non, gardons-les pour l'instant sauf si UX demande.
            // UX: Si on décoche une catégorie, on devrait masquer son dropdown, mais garder la sélection service ? 
            // Le user a dit "selectionner dabord... et une fois choisi un menu deroulant".
        } else {
            setSelectedCategoryIds(prev => [...prev, catId]);
        }
    };

    const toggleService = (service) => {
        const isSelected = selectedServices.find(s => s.id === service.id);
        if (isSelected) {
            setSelectedServices(prev => prev.filter(s => s.id !== service.id));
        } else {
            setSelectedServices(prev => [...prev, service]);
        }
    };

    const getTotalDuration = () => {
        return selectedServices.reduce((acc, s) => acc + s.duration, 0);
    };

    const validateStep1 = () => {
        if (selectedServices.length === 0) {
            setError("Veuillez sélectionner au moins un service.");
            return;
        }
        setError(null);
        setStep(2);
    };

    // Step 2 handlers
    const handleSlotSelect = useCallback(({ date, time }) => {
        if (date) {
            setFormData(prev => ({ ...prev, date, time: time || null }));
        }
    }, []);

    const handleDateValidation = () => {
        if (formData.date && formData.time) {
            setStep(3);
        } else {
            setError("Veuillez sélectionner une date et un horaire.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const [hours, minutes] = formData.time.split(':');
            const finalDate = new Date(formData.date);
            finalDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            // Construction de la note avec tous les services
            const servicesSummary = selectedServices.map(s => s.title).join(' + ');
            const fullNotes = `Services: ${servicesSummary}\nNote client: ${formData.notes}`;

            // On prend le premier service comme "Principal" pour la relation BDD
            // Idéalement BDD supporte multiple, sinon on fraude un peu
            const mainServiceId = selectedServices[0].id;

            const bookingData = {
                date: finalDate.toISOString(),
                service: mainServiceId, // Relation
                client_name: formData.client_name,
                client_email: formData.client_email,
                client_phone: formData.client_phone,
                notes: fullNotes,
                status: 'pending',
                user: user?.id || null
            };

            await createBooking(bookingData);
            setSuccess(true);
            setStep(4);

        } catch (err) {
            console.error(err);
            setError("Une erreur est survenue. Réessayez.");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setStep(1);
        setSelectedServices([]);
        setSelectedCategoryIds([]);
        setFormData({
            date: null,
            time: null,
            client_name: user?.name || '',
            client_email: user?.email || '',
            client_phone: '',
            notes: ''
        });
        setSuccess(false);
        setError(null);
    };

    if (success) {
        return (
            <div className="booking-form-pb booking-success-view">
                <FiCheckCircle className="success-icon" />
                <h3>Réservation confirmée !</h3>
                <p>Merci <strong>{formData.client_name}</strong>.</p>
                <div className="booking-summary-card">
                    <p><strong><FiScissors /> Services :</strong> {selectedServices.map(s => s.title).join(', ')}</p>
                    <p><strong><FiCalendar /> Date :</strong> {new Date(formData.date).toLocaleDateString()} à {formData.time}</p>
                </div>
                <div className="success-actions">
                    <button onClick={resetForm} className="btn-secondary">Nouveau RDV</button>
                    <button onClick={() => navigate('/')} className="btn-primary">Retour à l'accueil</button>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-form-pb">
            {/* Steps */}
            <div className="booking-steps">
                <div className={`step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
                <div className="step-line"></div>
                <div className={`step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
                <div className="step-line"></div>
                <div className={`step-dot ${step >= 3 ? 'active' : ''}`}>3</div>
            </div>

            {error && <div className="booking-error"><FiAlertCircle /> {error}</div>}

            {/* STEP 1: CATEGORIES & SERVICES */}
            {step === 1 && (
                <div className="step-content">
                    <h3>Choisissez vos prestations</h3>

                    {/* Catégories */}
                    <div className="categories-grid-select">
                        {categories.map(cat => (
                            <div
                                key={cat.id}
                                className={`category-card-select ${selectedCategoryIds.includes(cat.id) ? 'selected' : ''}`}
                                onClick={() => toggleCategory(cat.id)}
                            >
                                {/* Icône ou première lettre si pas d'icone */}
                                <div className="cat-icon-placeholder">{cat.name.charAt(0)}</div>
                                <span>{cat.name}</span>
                                {selectedCategoryIds.includes(cat.id) && <FiCheckCircle className="check-icon" />}
                            </div>
                        ))}
                    </div>

                    {/* Services par Catégorie Sélectionnée */}
                    <div className="services-selection-area">
                        {selectedCategoryIds.length > 0 ? (
                            selectedCategoryIds.map(catId => {
                                const cat = categories.find(c => c.id === catId);
                                const catServices = services.filter(s => s.expand?.category?.id === catId);

                                return (
                                    <div key={catId} className="services-group">
                                        <h4>{cat.name}</h4>
                                        <div className="services-list">
                                            {catServices.map(service => (
                                                <div
                                                    key={service.id}
                                                    className={`service-item-row ${selectedServices.find(s => s.id === service.id) ? 'selected' : ''}`}
                                                    onClick={() => toggleService(service)}
                                                >
                                                    <div className="service-row-info">
                                                        <span className="service-name">{service.title}</span>
                                                        <span className="service-meta">{service.duration} min • {service.price} €</span>
                                                    </div>
                                                    <div className="service-checkbox">
                                                        {selectedServices.find(s => s.id === service.id) ? <FiCheckCircle /> : <div className="circle-empty"></div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="helper-text-select-cat">👆 Sélectionnez une catégorie ci-dessus pour voir les services.</p>
                        )}
                    </div>

                    {/* Footer Recap */}
                    <div className="step-footer-actions">
                        <div className="selection-summary">
                            <span>{selectedServices.length} service(s) sélectionné(s)</span>
                            <strong>Total : {getTotalDuration()} min</strong>
                        </div>
                        <button
                            className="btn-next"
                            disabled={selectedServices.length === 0}
                            onClick={validateStep1}
                        >
                            Suivant
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 2: CALENDAR */}
            {step === 2 && (
                <div className="step-content">
                    <h3>
                        <button className="back-link" onClick={() => setStep(1)}>← Retour</button>
                        Choisissez un créneau
                    </h3>
                    <div className="selected-service-summary">
                        <strong>{selectedServices.map(s => s.title).join(' + ')}</strong>
                        <br />Durée totale : {getTotalDuration()} min
                    </div>

                    <BookingCalendar
                        onSlotSelect={handleSlotSelect}
                        selectedServiceDuration={getTotalDuration()}
                    />
                    <div className="step-actions">
                        <button className="btn-next" disabled={!formData.time} onClick={handleDateValidation}>Suivant</button>
                    </div>
                </div>
            )}

            {/* STEP 3: FORM */}
            {step === 3 && (
                <div className="step-content">
                    <h3><button className="back-link" onClick={() => setStep(2)}>← Retour</button> Vos informations</h3>
                    <div className="booking-summary-small">
                        {selectedServices.length} prestation(s) • {new Date(formData.date).toLocaleDateString()} à {formData.time}
                    </div>

                    <form onSubmit={handleSubmit} className="details-form">
                        <div className="form-group-pb">
                            <label><FiUser /> Nom complet *</label>
                            <input type="text" required value={formData.client_name} onChange={e => setFormData({ ...formData, client_name: e.target.value })} placeholder="Votre nom" />
                        </div>
                        <div className="form-group-pb">
                            <label><FiMail /> Email *</label>
                            <input type="email" required value={formData.client_email} onChange={e => setFormData({ ...formData, client_email: e.target.value })} placeholder="email@exemple.com" />
                        </div>
                        <div className="form-group-pb">
                            <label><FiSmartphone /> Téléphone *</label>
                            <input type="tel" required value={formData.client_phone} onChange={e => setFormData({ ...formData, client_phone: e.target.value })} placeholder="06..." />
                        </div>
                        <div className="form-group-pb">
                            <label><FiMessageSquare /> Notes</label>
                            <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Préférences..." rows={3} />
                        </div>
                        <button type="submit" className="btn-confirm" disabled={loading}>{loading ? "..." : "Confirmer le RDV"}</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default BookingFormPB;
